import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, FlaskConical } from "lucide-react";

function Counter({ target, suffix = "", label, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <div ref={ref} className="flex flex-col" data-testid={testId}>
      <div className="font-display text-4xl sm:text-5xl font-semibold text-slate-900 tabular-nums">
        {val.toLocaleString()}
        <span className="text-green-600">{suffix}</span>
      </div>
      <div className="mt-1 text-[0.72rem] font-medium tracking-[0.24em] text-slate-500 uppercase">
        {label}
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="top" className="relative pt-28 pb-24 lg:pt-36 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-70 -z-10" />
      <div className="absolute -top-32 -right-24 h-[520px] w-[520px] rounded-full bg-gradient-to-br from-blue-100 via-blue-50 to-white blur-3xl -z-10" />
      <div className="absolute -bottom-24 -left-24 h-[420px] w-[420px] rounded-full bg-gradient-to-tr from-green-100 to-white blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200/70 bg-white/70 backdrop-blur px-4 py-1.5 mb-6" data-testid="hero-eyebrow">
            <Sparkles size={14} className="text-green-600" />
            <span className="text-xs font-medium tracking-widest uppercase text-blue-900">
              Andrich Pharmaceuticals Pvt. Ltd.
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-slate-900 leading-[1.02]" data-testid="hero-heading">
            Discovering <span className="text-gradient">Light</span> for Life.
          </h1>

          <p className="mt-6 max-w-2xl text-lg text-slate-600 leading-relaxed" data-testid="hero-subheading">
            Delivering innovative pharmaceutical solutions with a commitment to quality, research,
            and better healthcare across India.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a href="#products" className="btn-primary" data-testid="hero-cta-products">
              Explore Products <ArrowRight size={16} className="ml-2" />
            </a>
            <a href="#contact" className="btn-secondary" data-testid="hero-cta-contact">
              Contact Us
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 sm:gap-10 max-w-2xl border-t border-slate-200 pt-8">
            <Counter target={5000} suffix="+" label="Working Partners" testId="stat-partners" />
            <Counter target={10} suffix="+" label="States Covered" testId="stat-states" />
            <Counter target={70} suffix="+" label="Products" testId="stat-products" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-blue-900/10 border border-white/70">
            <img
              src="https://images.unsplash.com/photo-1630959305606-3123a081dada?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
              alt="Modern pharmaceutical laboratory"
              className="w-full h-[560px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-950/40 via-transparent to-transparent" />
          </div>

          <div className="absolute -left-6 top-10 hidden md:flex animate-float-slow items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-xl px-4 py-3">
            <div className="h-10 w-10 rounded-xl bg-blue-900 text-white flex items-center justify-center">
              <FlaskConical size={18} />
            </div>
            <div>
              <div className="text-[0.7rem] tracking-[0.22em] uppercase text-slate-500">R&amp;D</div>
              <div className="font-display font-semibold text-slate-900">Research Driven</div>
            </div>
          </div>

          <div className="absolute -right-4 bottom-10 hidden md:flex animate-float items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-xl px-4 py-3">
            <div className="relative h-10 w-10 rounded-xl bg-green-600 text-white flex items-center justify-center">
              <ShieldCheck size={18} />
              <span className="absolute inset-0 rounded-xl animate-pulse-ring" />
            </div>
            <div>
              <div className="text-[0.7rem] tracking-[0.22em] uppercase text-slate-500">Quality</div>
              <div className="font-display font-semibold text-slate-900">WHO-GMP Standards</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
